import { Button } from 'antd';
import { Plus, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Navigate } from 'react-router';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageHeader } from '../../../shared/components/PageHeader';
import { TableSkeleton } from '../../../shared/components/TableSkeleton';
import { sectionTrail } from '../../../shared/components/section-trail';
import { CompanyTabs } from '../../company-settings/public/CompanyTabs';
import type { CompanyAccountView } from '../company-accounts.api';
import { buildCompanyAccountColumns } from '../components/build-company-account-columns';
import { CompanyAccountDrawer } from '../components/CompanyAccountDrawer';
import { CompanyAccountRowActions } from '../components/CompanyAccountRowActions';
import { CompanyAccountsTable } from '../components/CompanyAccountsTable';
import { useCompanyAccountActions } from '../hooks/use-company-account-actions';
import { useCompanyAccountList } from '../hooks/use-company-account-list';

/** Where customers pay: the bank accounts and M-Pesa numbers receipts can name. */
export function CompanyAccountsPage() {
  const { state, list, isRefreshing, reload } = useCompanyAccountList();
  const { error, saving, save, retire, remove, resetError } = useCompanyAccountActions(reload);
  const [drawer, setDrawer] = useState<{ open: boolean; editing?: CompanyAccountView }>({ open: false });

  if (state.status === 'forbidden') return <Navigate to="/" replace />;

  function openDrawer(editing?: CompanyAccountView) {
    resetError();
    setDrawer({ open: true, editing });
  }

  const columns = buildCompanyAccountColumns({
    list,
    actions: (account) => <CompanyAccountRowActions account={account} onEdit={openDrawer} onRetire={retire} onRemove={remove} />,
  });

  return (
    <>
      <PageHeader
        breadcrumbs={sectionTrail('company')}
        title="Company"
        description="The company this deployment serves: its particulars, locale, location and branding."
        actions={
          <Button type="primary" icon={<Plus className="size-4" />} onClick={() => openDrawer()}>
            Add account
          </Button>
        }
      />
      <CompanyTabs active="accounts" />
      {state.status === 'error' && (
        <EmptyState icon={ShieldAlert} title="Couldn't load accounts" description="Something went wrong loading the accounts. Try reloading the page." />
      )}
      {state.status === 'loading' && <TableSkeleton label="Loading accounts" withSearch />}
      {state.status === 'loaded' && <CompanyAccountsTable page={state.page} columns={columns} list={list} isRefreshing={isRefreshing} />}
      <CompanyAccountDrawer
        open={drawer.open}
        editing={drawer.editing}
        error={error}
        submitting={saving}
        onSave={(input) => save(drawer.editing, input)}
        onClose={() => setDrawer({ open: false })}
      />
    </>
  );
}
